import { useState } from 'react'
import { useFetchData } from '../../hooks/useFetchData'
import { evaluateTextAnswer } from '../../utils/evaluator'
import { useAutoSave } from '../../hooks/useDebouncedSave'
import { Loader, AlertCircle, Save, CheckCircle } from 'lucide-react'

export default function SRTPractice({ state, updateState }) {
  const { data, loading, error } = useFetchData('/data/srt_questions.json')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [response, setResponse] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [evaluation, setEvaluation] = useState(null)
  const [showIdeal, setShowIdeal] = useState(false)

  const situations = data?.situations || []
  const currentSituation = situations[currentIndex]

  // Auto-save response to drafts
  useAutoSave(
    response,
    (text) => {
      if (currentSituation && text) {
        updateState(`drafts.srt.${currentSituation.id}`, text)
      }
    },
    2000
  )

  const handleSubmit = () => {
    if (!response.trim()) return

    const result = evaluateTextAnswer(response, currentSituation.rubric)
    setEvaluation(result)
    setSubmitted(true)

    // Save to progress
    updateState('progress.srt.responses', [
      ...(state.progress.srt?.responses || []),
      {
        situationId: currentSituation.id,
        response,
        score: result.score,
        timestamp: new Date().toISOString(),
      },
    ])
    updateState('progress.srt.totalResponses', (state.progress.srt?.totalResponses || 0) + 1)
  }

  const handleNext = () => {
    setResponse('')
    setSubmitted(false)
    setEvaluation(null)
    setShowIdeal(false)
    setCurrentIndex((currentIndex + 1) % situations.length)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2">Loading situations...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-800 dark:text-red-200">Error Loading Situations</h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!currentSituation) return null

  const wordCount = response.trim().split(/\s+/).filter((w) => w.length > 0).length

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">SRT Practice</h2>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Situation {currentIndex + 1} of {situations.length}
        </div>
      </div>

      <div className="card bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500">
        <h3 className="font-bold mb-2">Instructions</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Read the situation carefully and write your immediate reaction. Be honest, practical, and show officer-like qualities.
          Keep your response concise (30-50 words).
        </p>
      </div>

      <div className="card">
        <div className="mb-4">
          <span className="text-xs text-gray-500 dark:text-gray-400">Situation ID: {currentSituation.id}</span>
        </div>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg mb-6">
          <p className="text-gray-800 dark:text-gray-200 leading-relaxed">
            {currentSituation.text}
          </p>
        </div>

        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold">Your Response</h3>
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <Save className="w-4 h-4 mr-1" />
            <span>{wordCount} words</span>
          </div>
        </div>

        <textarea
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          placeholder="Write your immediate reaction to this situation..."
          className="textarea-field"
          rows="4"
          disabled={submitted}
        />

        {!submitted && (
          <button
            onClick={handleSubmit}
            disabled={!response.trim()}
            className="btn-primary w-full mt-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Submit Response
          </button>
        )}
      </div>

      {/* Evaluation */}
      {submitted && evaluation && (
        <div className="space-y-4 animate-slide-in">
          <div className="card bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500">
            <h3 className="font-bold text-lg mb-3">Your Evaluation</h3>
            
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">Score</span>
                <span className="text-2xl font-bold text-green-600">
                  {evaluation.score}/100
                </span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill bg-green-600" style={{ width: `${evaluation.score}%` }} />
              </div>
            </div>

            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              {evaluation.feedback}
            </p>

            {evaluation.highlights.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-semibold mb-2">Key Points Covered:</p>
                <div className="flex flex-wrap gap-2">
                  {evaluation.highlights.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs rounded"
                    >
                      <CheckCircle className="w-3 h-3 inline mr-1" />
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {evaluation.missingKeywords.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Consider Including:</p>
                <div className="flex flex-wrap gap-2">
                  {evaluation.missingKeywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <button
              onClick={() => setShowIdeal(!showIdeal)}
              className="btn-secondary w-full mb-3"
            >
              {showIdeal ? 'Hide' : 'Show'} Ideal Response
            </button>

            {showIdeal && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg animate-slide-in">
                <h4 className="font-semibold mb-2 text-blue-800 dark:text-blue-200">
                  Ideal Response:
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {currentSituation.ideal_response}
                </p>
                
                {currentSituation.rubric.qualities_tested && (
                  <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                    <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-2">
                      Qualities Tested:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {currentSituation.rubric.qualities_tested.map((quality, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded"
                        >
                          {quality}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <button onClick={handleNext} className="btn-primary w-full">
            Next Situation
          </button>
        </div>
      )}
    </div>
  )
}
